import moment from "moment";
import type { HistoricNarRecommendation } from "../../../../../api/services/historicNar/types";
import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { getHistoricNarIngestionStateBadge } from "../../../../../enums/historicNarIngestionState";
import { HistoricNarConfigTableColumnKeys } from "../../../../../enums/tableColumnKeys";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../../utils/stringModifications";
import { NarAgentGridOrgTab } from "../../narAgentGrid/hooks/useNarAgentGridOrgTabs";

import {
  determineWhichIconToShow,
  formatScore,
  generateDisplayTextWithPrefixLabel,
  readVolume,
  showInfoIcon,
} from "./historicNarConfigTableUtils";
import { getPrimarySource } from "./historicNarSourceUtils";

export const HistoricNarConfigTableHeaders: TableHeader[] = [
  {
    label: "Payer",
    key: HistoricNarConfigTableColumnKeys.Payer,
    order: 1,
    width: 12,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "LOB",
    key: HistoricNarConfigTableColumnKeys.Lob,
    order: 2,
    width: 9,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "J Code",
    key: HistoricNarConfigTableColumnKeys.JCode,
    order: 3,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Drug Name",
    key: HistoricNarConfigTableColumnKeys.DrugName,
    order: 4,
    width: 16,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Month",
    key: HistoricNarConfigTableColumnKeys.Month,
    order: 5,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Volume",
    key: HistoricNarConfigTableColumnKeys.Volume,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Score",
    key: HistoricNarConfigTableColumnKeys.Score,
    order: 7,
    width: 7,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "State",
    key: HistoricNarConfigTableColumnKeys.Status,
    order: 9,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE_WITH_I,
  },
  {
    label: "Source",
    key: HistoricNarConfigTableColumnKeys.Source,
    order: 8,
    width: 16,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: HistoricNarConfigTableColumnKeys.Action,
    order: 10,
    width: 6,
    sortable: false,
    type: TableCellType.ICON,
  },
];

/**
 * Map historic NAR recommendation rows into `CustomTable`-ready row objects.
 *
 * @input rows: recommendations returned by the list endpoint
 * @input clientOptions: organization IDs and labels for automation tooltips
 * @return array of column-keyed row objects with ingestion-state badges and
 *   state-specific action icons; action clicks receive the original recommendation
 */
export const generateHistoricNarConfigTableData = (
  rows: HistoricNarRecommendation[],
  clientOptions: NarAgentGridOrgTab[],
) =>
  rows.map((row) => ({
    [HistoricNarConfigTableColumnKeys.Payer]:
      capitalizeWordsSeperatedByUnderScore(row?.payer ?? ""),
    [HistoricNarConfigTableColumnKeys.Lob]:
      capitalizeWordsSeperatedByUnderScore(row?.line_of_business ?? ""),
    [HistoricNarConfigTableColumnKeys.JCode]: row?.j_code ?? "",
    [HistoricNarConfigTableColumnKeys.DrugName]: row?.medicine_name ?? "",
    [HistoricNarConfigTableColumnKeys.Month]: row?.month
      ? moment(row.month, ["YYYY-MM", "MM/YYYY", moment.ISO_8601]).format(
          "MM/YYYY",
        )
      : "",
    [HistoricNarConfigTableColumnKeys.Volume]: readVolume(
      row?.volume_analytics,
    ),
    [HistoricNarConfigTableColumnKeys.Score]: formatScore(row?.score),
    // Backend ingestion state determines the badge; automation states expose client details.
    [HistoricNarConfigTableColumnKeys.Status]: {
      text: getHistoricNarIngestionStateBadge(row?.status ?? "No State").label,
      color: getHistoricNarIngestionStateBadge(row?.status ?? "").textColor,
      bgColor: getHistoricNarIngestionStateBadge(row?.status ?? "").bgColor,
      displayText: generateDisplayTextWithPrefixLabel(row, clientOptions),
      maxWidth: "200px",
      showInfoIcon: showInfoIcon(row?.status),
    },
    // Source column: the first recorded source phrase (dx code stripped); blank
    // when no source_text was captured.
    [HistoricNarConfigTableColumnKeys.Source]:
      getPrimarySource(row?.volume_analytics?.source_text) || "—",
    [HistoricNarConfigTableColumnKeys.Action]: {
      variant: determineWhichIconToShow(row?.status ?? ""),
      rowData: row,
    },
  }));
